import { useEffect, useRef, type ReactNode } from "react";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type HTMLMotionProps,
} from "framer-motion";
import { duration, ease } from "@rekati/tokens";
import { cn } from "@rekati/utils";

/**
 * Rises into place once, on mount or when scrolled into view. With reduced
 * motion it only fades, so nothing travels across the screen.
 */
export function Reveal({
  children,
  delay = 0,
  y = 12,
  className,
  inView = false,
  as = "div",
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  inView?: boolean;
  as?: "div" | "section" | "li" | "header" | "p" | "span";
}) {
  const reduce = useReducedMotion();
  const Comp = motion[as];
  const from = { opacity: 0, y: reduce ? 0 : y };
  const to = { opacity: 1, y: 0 };
  const transition = { duration: 0.4, ease: ease.outExpo, delay };
  return inView ? (
    <Comp
      className={className}
      initial={from}
      whileInView={to}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={transition}
    >
      {children}
    </Comp>
  ) : (
    <Comp className={className} initial={from} animate={to} transition={transition}>
      {children}
    </Comp>
  );
}

/**
 * A headline whose words rise from below a mask in sequence, the way a
 * departure board settles. The full sentence is one accessible string.
 */
export function SplitHeadline({
  text,
  className,
  wordClassName,
  delay = 0,
  stagger = 0.06,
  as: Tag = "h1",
}: {
  text: string;
  className?: string;
  wordClassName?: (word: string, index: number) => string | undefined;
  delay?: number;
  stagger?: number;
  as?: "h1" | "h2" | "p";
}) {
  const reduce = useReducedMotion();
  const words = text.split(/\s+/);
  return (
    <Tag className={className} aria-label={text}>
      {words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          className="inline-flex overflow-hidden pb-[0.08em] align-bottom"
          aria-hidden="true"
        >
          <motion.span
            className={cn("inline-block", wordClassName?.(w, i))}
            initial={{ y: reduce ? 0 : "105%", opacity: reduce ? 0 : 1 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: duration.scene, ease: ease.outExpo, delay: delay + i * stagger }}
          >
            {w}
          </motion.span>
          {i < words.length - 1 ? <span>&nbsp;</span> : null}
        </span>
      ))}
    </Tag>
  );
}

/**
 * A figure that counts to its value when it changes or scrolls into view, in
 * the style of Smooth UI's number ticker. Screen readers get the value once,
 * not every frame.
 */
export function NumberTicker({
  value,
  format = (n) => String(n),
  className,
}: {
  value: number;
  format?: (n: number) => string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useInView(ref, { once: true });
  const reduce = useReducedMotion();
  const mv = useMotionValue(0);
  const shown = useTransform(mv, (v) => format(Math.round(v)));

  useEffect(() => {
    if (!seen) return;
    if (reduce) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, { duration: 1.2, ease: ease.outExpo });
    return () => controls.stop();
  }, [seen, value, reduce, mv]);

  return (
    <span className={cn("tabular-nums", className)}>
      <motion.span ref={ref} aria-hidden="true">
        {shown}
      </motion.span>
      <span className="sr-only">{format(value)}</span>
    </span>
  );
}

/** Children appear one after another; pair with `StaggerItem`. */
export function Stagger({
  children,
  className,
  delay = 0,
  gap = 0.07,
  ...rest
}: HTMLMotionProps<"div"> & { delay?: number; gap?: number }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="shown"
      variants={{ hidden: {}, shown: { transition: { staggerChildren: gap, delayChildren: delay } } }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className, ...rest }: HTMLMotionProps<"div">) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: reduce ? 0 : 14 },
        shown: { opacity: 1, y: 0, transition: { duration: duration.slow, ease: ease.outExpo } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export { SignalTrace } from "./signal-trace";
